import { ref, computed, Ref } from 'vue'

export interface UseLoadMoreOptions {
  /** 初始页码 */
  initialPage?: number
  /** 每页数量 */
  pageSize?: number
  /** 初始数据 */
  initialData?: any[]
}

export interface UseLoadMoreReturn<T = any> {
  /** 当前页码 */
  pageNum: Ref<number>
  /** 每页数量 */
  pageSize: Ref<number>
  /** 数据列表 */
  data: Ref<T[]>
  /** 总数 */
  total: Ref<number>
  /** 终端总数（授权上限判断用，由接口透传） */
  terminal_count: Ref<number>
  /** 是否正在加载 */
  loading: Ref<boolean>
  /** 是否还有更多数据 */
  hasMore: Ref<boolean>
  /** 加载更多 */
  loadMore: (fetchFn: (page: number, pageSize: number) => Promise<{ data: T[], total: number, terminal_count?: number }>) => Promise<void>
  /** 重置（用于查询时重置数据） */
  reset: () => void
  /** 设置总数 */
  setTotal: (total: number) => void
  /** 追加数据 */
  appendData: (newData: T[]) => void
  /** 设置数据 */
  setData: (newData: T[]) => void
}

/**
 * 加载更多数据管理 Hook
 * @param options 配置选项
 * @returns 加载更多相关的状态和方法
 */
export function useLoadMore<T = any>(options: UseLoadMoreOptions = {}): UseLoadMoreReturn<T> {
  const {
    initialPage = 1,
    pageSize: initialPageSize = 20,
    initialData = []
  } = options

  const pageNum = ref(initialPage)
  const pageSize = ref(initialPageSize)
  const data = ref<T[]>(initialData) as Ref<T[]>
  const total = ref(0)
  const terminal_count = ref(0)
  const loading = ref(false)
  // 标记是否已经加载过数据（用于区分初始状态和加载后为空的状态）
  const hasLoaded = ref(initialData.length > 0)

  // 计算是否还有更多数据
  const hasMore = computed(() => {
    // 如果还没有加载过数据（且初始数据为空），允许首次加载
    if (!hasLoaded.value && data.value.length === 0) {
      return true
    }
    // 如果total为0，说明没有更多数据了
    if (total.value === 0) {
      return false
    }
    return data.value.length < total.value
  })

  /**
   * 加载更多数据
   * @param fetchFn 获取数据的函数，返回 { data, total }
   */
  const loadMore = async (fetchFn: (page: number, pageSize: number) => Promise<{ data: T[], total: number, terminal_count:number }>) => {
    if (!hasMore.value || loading.value) return
    loading.value = true
    const currentDataLength = data.value.length

    try {
      // 先调用获取数据的函数，使用当前页码
      const result = await fetchFn(pageNum.value, pageSize.value)

      // 标记已经加载过数据
      hasLoaded.value = true

      // 更新总数
      total.value = result.total

      // 更新终端总数（如有）
      terminal_count.value = result.terminal_count || 0

      // 追加数据
      data.value = [...data.value, ...result.data]

      // 如果数据没有增加且仍然有更多数据，说明可能出错了，不增加页码
      if (data.value.length === currentDataLength && hasMore.value) {
        // 数据没有增加，不增加页码
      } else {
        // 数据加载成功，页码+1，为下次加载做准备
        pageNum.value = pageNum.value + 1
      }
    } catch (error) {
      // 出错时不改变页码
      throw error
    } finally {
      loading.value = false
    }
  }

  /**
   * 重置数据（用于查询时）
   */
  const reset = () => {
    pageNum.value = initialPage
    data.value = []
    total.value = 0
    terminal_count.value = 0
    hasLoaded.value = false
    loading.value = false
  }

  /**
   * 设置总数
   */
  const setTotal = (newTotal: number) => {
    total.value = newTotal
  }

  /**
   * 追加数据
   */
  const appendData = (newData: T[]) => {
    data.value = [...data.value, ...newData]
  }

  /**
   * 设置数据
   */
  const setData = (newData: T[]) => {
    data.value = newData
  }

  return {
    pageNum,
    pageSize,
    data,
    total,
    terminal_count,
    loading,
    hasMore,
    loadMore,
    reset,
    setTotal,
    appendData,
    setData
  }
}


